import { IEvent } from '@/types'
import { getEventUiState } from '@/utils/events/getEventUiState'

/**
 * Single place that answers "what kind of playlist item is this, and is it
 * live". Playlist items reach us in three shapes:
 *
 * - 24/7 live channels: `_model: 'Content'` with `isLiveChannel: true`, no
 *   event, and `live: false` — the `live` flag is NOT the channel signal.
 * - Events: usually `_model: 'Content'` wrapping the event under
 *   `__belong.Event`; event playlists instead return the raw Event doc
 *   (`_model: 'Event'`, teams at top level, no title/images).
 * - Everything else: plain content.
 *
 * Surfaces read `kind`/`isLive` and decide their own presentation — this
 * module deliberately holds no copy, styling, or routing.
 */
export const ITEM_KIND = {
  CONTENT: 'content',
  EVENT: 'event',
  LIVE_CHANNEL: 'live-channel',
} as const

export type ItemKind = typeof ITEM_KIND[keyof typeof ITEM_KIND]

/** Structural view of the fields the classification reads. */
type ItemLike = {
  _model?: string
  isLiveChannel?: boolean
  live?: boolean
  homeTeam?: unknown
  videos?: unknown[]
  // Kept as `unknown`: `Belong` is an interface, so a narrower shape here would
  // reject every real IPlaylistItem. Read through `getEmbeddedEvent` instead.
  __belong?: unknown
}

const getEmbeddedEvent = (item?: ItemLike | null): IEvent | null =>
  (item?.__belong as { Event?: IEvent | null } | undefined)?.Event ?? null

export type ItemState = {
  kind: ItemKind
  /** The event doc when `kind === 'event'`, else null. */
  event: IEvent | null
  /** True for 24/7 channels (always live) and for events live by any signal. */
  isLive: boolean
  /** The item is the Event doc itself rather than a content wrapper. */
  isRawEventDoc: boolean
  /**
   * Whether the item has playable videos. Read from the item only — never from
   * an embedded event — because that is the array `Cards/index.tsx` hands to
   * `resolveNavigation`, so this matches where a click actually lands. A
   * content wrapper with no videos routes to the detail page even when the
   * event it references has some.
   */
  hasVideos: boolean
}

/**
 * True when the item IS an Event doc, not a content item referencing one.
 * Raw docs have no title/images, so surfaces may need to render them differently.
 */
export const isRawEventDoc = (item?: ItemLike | null): boolean =>
  item?._model?.toLowerCase() === 'event' || !!item?.homeTeam

/** The event a playlist item carries, whichever shape it arrived in. */
export const getItemEvent = (item?: ItemLike | null): IEvent | null => {
  const embedded = getEmbeddedEvent(item)
  if (embedded) return embedded

  return isRawEventDoc(item) ? (item as IEvent) : null
}

export const isLiveChannelItem = (item?: ItemLike | null): boolean => item?.isLiveChannel === true

export const getItemState = (item?: ItemLike | null): ItemState => {
  const hasVideos = (item?.videos?.length ?? 0) > 0

  // Channel wins: a 24/7 channel is always live, whatever else it carries.
  if (isLiveChannelItem(item)) {
    return { kind: ITEM_KIND.LIVE_CHANNEL, event: null, isLive: true, isRawEventDoc: false, hasVideos }
  }

  const event = getItemEvent(item)

  if (event) {
    // getEventUiState reconciles programmingData.status with eventStatus.category.
    return {
      kind: ITEM_KIND.EVENT,
      event,
      isLive: getEventUiState(event).isLive,
      isRawEventDoc: isRawEventDoc(item),
      hasVideos,
    }
  }

  return { kind: ITEM_KIND.CONTENT, event: null, isLive: item?.live === true, isRawEventDoc: false, hasVideos }
}
